import React, { useEffect, useMemo, useState } from "react";
import api, { formatError } from "@/lib/api";
import { toast } from "@/components/Toaster";
import { PERM, TPL } from "@/constants/testIds";
import { PERMISSION_KEYS, PERMISSION_LABELS } from "@/constants/permissions";
import {
  Sparkles, Users as UsersIcon, FileText, Search, Shield, CheckCircle2, Circle, Copy, X,
  AlertTriangle, RotateCcw, Save,
} from "lucide-react";

export default function PermissionsTab({ roomId }) {
  const [docs, setDocs] = useState([]);
  const [members, setMembers] = useState([]);
  const [perms, setPerms] = useState({});           // current (edited) state
  const [originalPerms, setOriginalPerms] = useState({}); // baseline from server
  const [templates, setTemplates] = useState({ system: [], custom: [] });
  const [showApply, setShowApply] = useState(false);
  const [showBulk, setShowBulk] = useState(false);
  const [showReview, setShowReview] = useState(false);  // review-and-apply modal
  const [applying, setApplying] = useState(false);
  const [bulkForm, setBulkForm] = useState({ source_user_id: "", target_user_id: "", scope: "all" });
  const [bulkMsg, setBulkMsg] = useState({ kind: "", text: "" });
  const [bulkLoading, setBulkLoading] = useState(false);
  const [applyForm, setApplyForm] = useState({ user_id: "", template_id: "", doc_id: null });
  const [applyMsg, setApplyMsg] = useState("");

  // Focused view state
  const [mode, setMode] = useState("member");        // "member" | "document"
  const [memberId, setMemberId] = useState("");      // selected member
  const [docId, setDocId] = useState("");            // selected document
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(true);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [d, m, p, t] = await Promise.all([
        api.get(`/rooms/${roomId}/documents`),
        api.get(`/rooms/${roomId}/members`),
        api.get(`/rooms/${roomId}/permissions`),
        api.get(`/permission-templates`),
      ]);
      setDocs(d.data.filter((x) => x.type === "file"));
      setMembers(m.data);
      const map = {};
      p.data.forEach((row) => {
        const k = `${row.user_id}:${row.doc_id || "room"}`;
        map[k] = row;
      });
      setPerms(map);
      // Deep clone so editing `perms` doesn't mutate the baseline used to
      // diff staged changes. JSON round-trip is fine here — rows are plain
      // {key: bool} objects with no Dates/Maps/etc.
      setOriginalPerms(JSON.parse(JSON.stringify(map)));
      setTemplates(t.data);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { loadAll(); }, [roomId]);

  // Default the selectors so the page doesn't feel empty
  useEffect(() => {
    if (mode === "member" && !memberId && members[0]) setMemberId(members[0].user_id);
    if (mode === "document" && !docId && docs[0]) setDocId(docs[0].id);
  }, [mode, members, docs]); // eslint-disable-line

  // Stage a single permission toggle in-memory. No POST until the admin
  // hits "Apply N changes" — they get a chance to review the full diff
  // (including any rebound flips that net out to no change).
  const toggle = (uid, dId, key) => {
    const k = `${uid}:${dId || "room"}`;
    const current = perms[k] || {};
    const next = { ...current, [key]: !current[key] };
    setPerms({ ...perms, [k]: next });
  };

  // Compute the diff between `perms` and `originalPerms` per-permission so
  // the review modal can show "X · Download · ENABLED" rows instead of a
  // confusing per-row blob. Permission flips that cancel out (off → on → off)
  // simply don't appear here.
  const pendingChanges = useMemo(() => {
    const out = [];
    const keys = new Set([...Object.keys(perms), ...Object.keys(originalPerms)]);
    keys.forEach((k) => {
      const cur = perms[k] || {};
      const orig = originalPerms[k] || {};
      PERMISSION_KEYS.forEach((pk) => {
        const a = !!orig[pk];
        const b = !!cur[pk];
        if (a !== b) {
          const [uid, sc] = k.split(":");
          out.push({
            rowKey: k,
            uid,
            docId: sc === "room" ? null : sc,
            perm: pk,
            from: a,
            to: b,
          });
        }
      });
    });
    return out;
  }, [perms, originalPerms]);

  const pendingByRow = useMemo(() => {
    const m = new Map();
    pendingChanges.forEach((c) => {
      if (!m.has(c.rowKey)) m.set(c.rowKey, []);
      m.get(c.rowKey).push(c);
    });
    return m;
  }, [pendingChanges]);

  // Best-effort warning when the admin tries to navigate / refresh away
  // with unsaved changes. Won't fire on in-app route changes (those need
  // a router-level guard) but does catch tab close and full reloads.
  useEffect(() => {
    if (pendingChanges.length === 0) return undefined;
    const beforeUnload = (e) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", beforeUnload);
    return () => window.removeEventListener("beforeunload", beforeUnload);
  }, [pendingChanges.length]);

  // Persist all staged changes. We POST one permissions row per affected
  // (user, doc) pair — the backend overwrites the existing row in full.
  const applyAll = async () => {
    if (pendingChanges.length === 0) return;
    setApplying(true);
    try {
      const affected = [...pendingByRow.keys()];
      await Promise.all(affected.map((k) => {
        const [uid, sc] = k.split(":");
        const dId = sc === "room" ? null : sc;
        const row = perms[k] || {};
        return api.post(`/rooms/${roomId}/permissions`, {
          user_id: uid,
          doc_id: dId,
          permissions: PERMISSION_KEYS.reduce((acc, p) => ({ ...acc, [p]: !!row[p] }), {}),
        });
      }));
      toast.success(
        `Applied ${pendingChanges.length} permission change${pendingChanges.length === 1 ? "" : "s"} across ${affected.length} scope${affected.length === 1 ? "" : "s"}.`
      );
      setShowReview(false);
      // Refresh from server so we re-sync the baseline.
      await loadAll();
    } catch (e) {
      toast.error(formatError(e));
    } finally {
      setApplying(false);
    }
  };

  const discardAll = () => {
    if (pendingChanges.length === 0) return;
    setPerms(JSON.parse(JSON.stringify(originalPerms)));
    setShowReview(false);
    toast.info(`Discarded ${pendingChanges.length} unsaved change${pendingChanges.length === 1 ? "" : "s"}.`);
  };

  const apply = async (e) => {
    e.preventDefault();
    setApplyMsg("");
    try {
      await api.post(`/rooms/${roomId}/permissions/apply-template`, applyForm);
      setApplyMsg("Template applied.");
      await loadAll();
      setTimeout(() => { setApplyMsg(""); setShowApply(false); }, 1200);
    } catch (e) { setApplyMsg(formatError(e)); }
  };

  const bulkApply = async (e) => {
    e.preventDefault();
    setBulkMsg({ kind: "", text: "" });
    setBulkLoading(true);
    try {
      const { data } = await api.post(`/rooms/${roomId}/permissions/copy-from-member`, bulkForm);
      setBulkMsg({ kind: "success", text: `Copied ${data.rows_copied} permission row(s).` });
      await loadAll();
      setTimeout(() => { setShowBulk(false); setBulkMsg({ kind: "", text: "" }); }, 1400);
    } catch (e) {
      setBulkMsg({ kind: "error", text: formatError(e) });
    } finally { setBulkLoading(false); }
  };

  // Build the rows of the focused table.
  // - mode=member: rows are [Room default, ...docs] for the selected member
  // - mode=document: rows are [each member] for the selected document
  const filterMatch = (txt) => !query || (txt || "").toLowerCase().includes(query.toLowerCase());
  let rows = [];
  let rowAxis = "";
  if (mode === "member" && memberId) {
    rows = [
      { kind: "room", id: null, label: "Room default", sub: "Applies to every file unless overridden" },
      ...docs
        .filter((d) => filterMatch(d.name))
        .map((d) => ({ kind: "doc", id: d.id, label: d.name, sub: "" })),
    ];
    rowAxis = "Scope";
  } else if (mode === "document") {
    rows = members
      .filter((m) => filterMatch(m.email) || filterMatch(m.name))
      .map((m) => ({ kind: "member", id: m.user_id, label: m.name || m.email, sub: m.email }));
    rowAxis = "Member";
  }

  const currentMember = members.find((m) => m.user_id === memberId);
  const currentDoc = docs.find((d) => d.id === docId);
  const cellsDisabled = mode === "member" ? !memberId : !docId;

  const cellFor = (row) => {
    if (mode === "member") return { uid: memberId, docId: row.id };
    return { uid: row.id, docId };
  };

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h3 className="font-display tracking-tight text-lg">Access &amp; Permissions</h3>
          <p className="text-sm text-muted-foreground mt-1">
            Pick a member to review their full access, or pick a file to see who can do what.
            Toggle cells to stage changes — they don&apos;t take effect until you
            click <span className="text-foreground">Apply</span>.
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <button
            data-testid="perm-bulk-open"
            onClick={() => setShowBulk((v) => !v)}
            className="inline-flex items-center gap-2 px-3 py-2 text-sm rounded-md border border-border hover:bg-secondary transition"
          >
            <Copy className="w-4 h-4" /> Bulk apply
          </button>
          <button
            data-testid={TPL.applyOpen}
            onClick={() => setShowApply((v) => !v)}
            className="inline-flex items-center gap-2 px-3 py-2 text-sm rounded-md bg-primary text-primary-foreground hover:bg-primary/90 transition"
          >
            <Sparkles className="w-4 h-4" /> Apply template
          </button>
        </div>
      </div>

      {/* Apply-template inline panel (cleaner layout) */}
      {showBulk && (
        <form
          onSubmit={bulkApply}
          className="border border-border rounded-md bg-card p-4 space-y-3"
          data-testid="perm-bulk-panel"
        >
          <div className="flex items-center justify-between gap-2">
            <div>
              <h4 className="font-display tracking-tight">Copy permissions from one member to another</h4>
              <p className="text-xs text-muted-foreground mt-1">
                Mirrors the source&apos;s room-level baseline and every per-document
                override onto the target. Existing overrides on the target are wiped first.
              </p>
            </div>
            <button
              type="button"
              onClick={() => { setShowBulk(false); setBulkMsg({ kind: "", text: "" }); }}
              className="p-1.5 rounded-md hover:bg-secondary transition shrink-0"
              aria-label="Close bulk apply"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
          <div className="grid md:grid-cols-3 gap-3">
            <div>
              <label className="label-mono">Copy from (source)</label>
              <select
                data-testid="perm-bulk-source"
                required
                value={bulkForm.source_user_id}
                onChange={(e) => setBulkForm({ ...bulkForm, source_user_id: e.target.value })}
                className="mt-1 w-full px-3 py-2 bg-background border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-ring"
              >
                <option value="">Select source…</option>
                {members.map((m) => (
                  <option key={m.user_id} value={m.user_id}>{m.email}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="label-mono">Apply to (target)</label>
              <select
                data-testid="perm-bulk-target"
                required
                value={bulkForm.target_user_id}
                onChange={(e) => setBulkForm({ ...bulkForm, target_user_id: e.target.value })}
                className="mt-1 w-full px-3 py-2 bg-background border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-ring"
              >
                <option value="">Select target…</option>
                {members
                  .filter((m) => m.user_id !== bulkForm.source_user_id)
                  .map((m) => (
                    <option key={m.user_id} value={m.user_id}>{m.email}</option>
                  ))}
              </select>
            </div>
            <div>
              <label className="label-mono">Scope</label>
              <select
                data-testid="perm-bulk-scope"
                value={bulkForm.scope}
                onChange={(e) => setBulkForm({ ...bulkForm, scope: e.target.value })}
                className="mt-1 w-full px-3 py-2 bg-background border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-ring"
              >
                <option value="all">Room default + per-doc overrides</option>
                <option value="room">Room default only</option>
                <option value="overrides">Per-doc overrides only</option>
              </select>
            </div>
          </div>
          {bulkMsg.text && (
            <div className={`text-xs ${bulkMsg.kind === "success" ? "text-accent" : "text-destructive"}`}>
              {bulkMsg.text}
            </div>
          )}
          <div className="flex items-center justify-end gap-2">
            <button
              type="button"
              onClick={() => { setShowBulk(false); setBulkMsg({ kind: "", text: "" }); }}
              className="px-3 py-2 text-sm rounded-md border border-border hover:bg-secondary transition"
            >
              Cancel
            </button>
            <button
              data-testid="perm-bulk-submit"
              type="submit"
              disabled={bulkLoading || !bulkForm.source_user_id || !bulkForm.target_user_id}
              className="px-4 py-2 rounded-md bg-primary text-primary-foreground hover:bg-primary/90 transition disabled:opacity-60"
            >
              {bulkLoading ? "Copying…" : "Copy permissions"}
            </button>
          </div>
        </form>
      )}

      {/* Apply-template inline panel (cleaner layout) */}
      {showApply && (
        <form
          onSubmit={apply}
          className="border border-border rounded-md bg-card p-4 space-y-3"
        >
          <div className="grid md:grid-cols-3 gap-3">
            <div>
              <label className="label-mono">Member</label>
              <select
                data-testid={TPL.applyMember}
                required value={applyForm.user_id}
                onChange={(e) => setApplyForm({ ...applyForm, user_id: e.target.value })}
                className="mt-1 w-full px-3 py-2 bg-background border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-ring"
              >
                <option value="">Select member…</option>
                {members.map((m) => (
                  <option key={m.user_id} value={m.user_id}>{m.email}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="label-mono">Template</label>
              <select
                data-testid={TPL.applyTemplate}
                required value={applyForm.template_id}
                onChange={(e) => setApplyForm({ ...applyForm, template_id: e.target.value })}
                className="mt-1 w-full px-3 py-2 bg-background border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-ring"
              >
                <option value="">Select template…</option>
                <optgroup label="System">
                  {templates.system.map((t) => (
                    <option key={t.id} value={t.id}>{t.name}</option>
                  ))}
                </optgroup>
                {templates.custom.length > 0 && (
                  <optgroup label="Custom">
                    {templates.custom.map((t) => (
                      <option key={t.id} value={t.id}>{t.name}</option>
                    ))}
                  </optgroup>
                )}
              </select>
            </div>
            <div className="flex items-end gap-2">
              <button
                data-testid={TPL.applySubmit}
                type="submit"
                className="px-4 py-2 rounded-md bg-primary text-primary-foreground hover:bg-primary/90 transition w-full md:w-auto"
              >
                Apply
              </button>
              <button
                type="button"
                onClick={() => { setShowApply(false); setApplyMsg(""); }}
                className="px-3 py-2 text-sm rounded-md border border-border hover:bg-secondary transition"
              >
                Cancel
              </button>
            </div>
          </div>
          {applyMsg && (
            <div className={`text-xs ${applyMsg === "Template applied." ? "text-accent" : "text-destructive"}`}>
              {applyMsg}
            </div>
          )}
          <p className="text-xs text-muted-foreground">
            Templates set the member&apos;s room-level baseline. Per-document overrides remain unchanged.
          </p>
        </form>
      )}

      {/* Toolbar: mode toggle + entity selector + filter */}
      <div className="flex flex-wrap items-center gap-3 border border-border rounded-md bg-card p-3">
        {/* segmented control */}
        <div className="inline-flex rounded-md border border-border bg-background p-0.5">
          {[
            { v: "member", label: "By member", Icon: UsersIcon },
            { v: "document", label: "By document", Icon: FileText },
          ].map(({ v, label, Icon }) => (
            <button
              key={v}
              type="button"
              data-testid={`perm-mode-${v}`}
              onClick={() => setMode(v)}
              className={`inline-flex items-center gap-2 px-3 py-1.5 text-sm rounded-md transition ${
                mode === v
                  ? "bg-primary text-primary-foreground"
                  : "text-muted-foreground hover:text-foreground"
              }`}
            >
              <Icon className="w-4 h-4" /> {label}
            </button>
          ))}
        </div>

        {/* primary entity selector */}
        {mode === "member" ? (
          <select
            value={memberId}
            onChange={(e) => setMemberId(e.target.value)}
            data-testid="perm-select-member"
            className="px-3 py-2 text-sm bg-background border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-ring min-w-[220px]"
          >
            <option value="">Select a member…</option>
            {members.map((m) => (
              <option key={m.user_id} value={m.user_id}>
                {m.name ? `${m.name} — ${m.email}` : m.email}
              </option>
            ))}
          </select>
        ) : (
          <select
            value={docId}
            onChange={(e) => setDocId(e.target.value)}
            data-testid="perm-select-doc"
            className="px-3 py-2 text-sm bg-background border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-ring min-w-[260px]"
          >
            <option value="">Select a document…</option>
            {docs.map((d) => (
              <option key={d.id} value={d.id}>{d.name}</option>
            ))}
          </select>
        )}

        {/* filter for rows */}
        <div className="relative ml-auto">
          <Search className="w-4 h-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={mode === "member" ? "Filter files…" : "Filter members…"}
            data-testid="perm-filter"
            className="pl-8 pr-3 py-2 text-sm bg-background border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-ring w-56"
          />
        </div>
      </div>

      {/* Context card showing whose access is on screen */}
      {!cellsDisabled && (
        <div className="flex items-center gap-3 px-4 py-3 rounded-md border border-border bg-card">
          <div className="w-9 h-9 rounded-md bg-primary/15 text-primary flex items-center justify-center">
            {mode === "member"
              ? <UsersIcon className="w-4 h-4" />
              : <FileText className="w-4 h-4" />}
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-sm font-medium truncate">
              {mode === "member"
                ? (currentMember?.name || currentMember?.email || "—")
                : (currentDoc?.name || "—")}
            </div>
            <div className="text-xs text-muted-foreground truncate">
              {mode === "member"
                ? `${currentMember?.email || ""} · ${currentMember?.role || "user"}`
                : `${rows.length} member${rows.length === 1 ? "" : "s"} with access`}
            </div>
          </div>
          <span className="hidden sm:inline-flex text-xs text-muted-foreground">
            {pendingChanges.length === 0
              ? "All changes saved"
              : `${pendingChanges.length} unsaved`}
          </span>
        </div>
      )}

      {/* Sticky "pending changes" bar — appears only when changes are staged.
          Sits just above the table so it's impossible to miss. */}
      {pendingChanges.length > 0 && (
        <div
          data-testid="perm-pending-bar"
          className="sticky top-0 z-20 flex flex-wrap items-center gap-3 px-4 py-2.5 rounded-md border border-amber-500/40 bg-amber-500/10 text-amber-100"
        >
          <span className="inline-flex items-center justify-center w-6 h-6 rounded-md bg-amber-500/20 text-amber-400 shrink-0">
            <AlertTriangle className="w-3.5 h-3.5" />
          </span>
          <div className="text-sm min-w-0 flex-1">
            <span className="text-foreground font-medium">
              {pendingChanges.length} unsaved change{pendingChanges.length === 1 ? "" : "s"}
            </span>
            <span className="text-muted-foreground ml-2">
              across {pendingByRow.size} scope{pendingByRow.size === 1 ? "" : "s"} — review and apply
              when you&apos;re ready.
            </span>
          </div>
          <div className="flex items-center gap-2 shrink-0 ml-auto">
            <button
              type="button"
              data-testid="perm-discard-btn"
              onClick={discardAll}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-md border border-border text-muted-foreground hover:bg-secondary hover:text-foreground transition"
            >
              <RotateCcw className="w-3.5 h-3.5" /> Discard
            </button>
            <button
              type="button"
              data-testid="perm-review-btn"
              onClick={() => setShowReview(true)}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-md bg-primary text-primary-foreground hover:bg-primary/90 transition"
            >
              <Save className="w-3.5 h-3.5" /> Review &amp; Apply
            </button>
          </div>
        </div>
      )}

      {/* Focused permissions table */}
      <div className="border border-border rounded-md overflow-hidden bg-card">
        <div className="overflow-x-auto">
          <table className="min-w-[760px] w-full text-sm">
            <thead>
              <tr className="bg-secondary/60">
                <th className="text-left px-4 py-3 label-mono text-muted-foreground sticky left-0 bg-secondary/60 z-[1]">
                  {rowAxis}
                </th>
                {PERMISSION_KEYS.map((k) => (
                  <th
                    key={k}
                    title={PERMISSION_LABELS[k].hint}
                    className="px-3 py-3 label-mono text-muted-foreground text-center whitespace-nowrap"
                  >
                    {PERMISSION_LABELS[k].label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr>
                  <td colSpan={1 + PERMISSION_KEYS.length} className="px-4 py-10 text-center text-sm text-muted-foreground">
                    Loading permissions…
                  </td>
                </tr>
              )}
              {!loading && cellsDisabled && (
                <tr>
                  <td colSpan={1 + PERMISSION_KEYS.length} className="px-4 py-10 text-center text-sm text-muted-foreground">
                    {mode === "member"
                      ? "Select a member to view and edit their access."
                      : "Select a document to view and edit who has access."}
                  </td>
                </tr>
              )}
              {!loading && !cellsDisabled && rows.length === 0 && (
                <tr>
                  <td colSpan={1 + PERMISSION_KEYS.length} className="px-4 py-10 text-center text-sm text-muted-foreground">
                    No {mode === "member" ? "files" : "members"} match your filter.
                  </td>
                </tr>
              )}
              {!loading && !cellsDisabled && rows.map((row) => {
                const { uid, docId: rDocId } = cellFor(row);
                const key = `${uid}:${rDocId || "room"}`;
                const rowPerms = perms[key] || {};
                const isRoomDefault = mode === "member" && row.kind === "room";
                return (
                  <tr
                    key={key}
                    className={`border-t border-border ${isRoomDefault ? "bg-primary/[0.04]" : "hover:bg-secondary/30"}`}
                  >
                    <td className="px-4 py-2.5 sticky left-0 bg-card group-hover:bg-secondary/30 z-[1]">
                      <div className="flex items-center gap-2 min-w-0">
                        {isRoomDefault ? (
                          <Shield className="w-4 h-4 text-primary shrink-0" />
                        ) : row.kind === "doc" ? (
                          <FileText className="w-4 h-4 text-muted-foreground shrink-0" />
                        ) : (
                          <UsersIcon className="w-4 h-4 text-muted-foreground shrink-0" />
                        )}
                        <div className="min-w-0">
                          <div className={`truncate ${isRoomDefault ? "font-medium" : ""}`}>
                            {row.label}
                          </div>
                          {row.sub && (
                            <div className="text-xs text-muted-foreground truncate">{row.sub}</div>
                          )}
                        </div>
                      </div>
                    </td>
                    {PERMISSION_KEYS.map((p) => {
                      const on = !!rowPerms[p];
                      const orig = !!(originalPerms[key] || {})[p];
                      const isPending = on !== orig;
                      return (
                        <td key={p} className="px-3 py-2 text-center">
                          <button
                            data-testid={PERM.cell}
                            data-pending={isPending || undefined}
                            onClick={() => toggle(uid, rDocId, p)}
                            title={
                              `${PERMISSION_LABELS[p].label} — ${PERMISSION_LABELS[p].hint}` +
                              (isPending ? `  ·  Pending (was ${orig ? "ON" : "OFF"} → ${on ? "ON" : "OFF"})` : "")
                            }
                            aria-pressed={on}
                            className={`relative inline-flex items-center justify-center w-7 h-7 rounded-md border transition ${
                              on
                                ? "bg-primary border-primary text-primary-foreground shadow-sm"
                                : "bg-background border-border hover:border-primary text-muted-foreground"
                            } ${isPending ? "ring-2 ring-amber-500/70 ring-offset-2 ring-offset-card" : ""}`}
                          >
                            {on ? <CheckCircle2 className="w-4 h-4" /> : <Circle className="w-3.5 h-3.5" />}
                            {isPending && (
                              <span className="absolute -top-1 -right-1 w-2 h-2 rounded-full bg-amber-400 ring-2 ring-card" />
                            )}
                          </button>
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Legend */}
      <div className="text-xs text-muted-foreground border border-dashed border-border rounded-md p-3 grid sm:grid-cols-2 md:grid-cols-3 gap-x-6 gap-y-1.5">
        {PERMISSION_KEYS.map((k) => (
          <div key={k} className="flex gap-2">
            <span className="text-foreground font-medium shrink-0">{PERMISSION_LABELS[k].label}:</span>
            <span className="truncate">{PERMISSION_LABELS[k].hint}</span>
          </div>
        ))}
      </div>

      {/* Review-and-apply modal — surfaces every staged change grouped by
          scope (room baseline / per-document override / per-member override)
          so the admin sees exactly what's about to be written. */}
      {showReview && (
        <ReviewModal
          changes={pendingChanges}
          byRow={pendingByRow}
          members={members}
          docs={docs}
          applying={applying}
          onCancel={() => setShowReview(false)}
          onApply={applyAll}
          onDiscard={discardAll}
        />
      )}
    </div>
  );
}

/* --------------------------- Review & Apply modal ----------------------- */

function ReviewModal({ changes, byRow, members, docs, applying, onCancel, onApply, onDiscard }) {
  const memberById = useMemo(() => {
    const m = new Map();
    members.forEach((x) => m.set(x.user_id, x));
    return m;
  }, [members]);
  const docById = useMemo(() => {
    const m = new Map();
    docs.forEach((x) => m.set(x.id, x));
    return m;
  }, [docs]);

  // Group rows for the modal: { memberLabel, scopeRows: [{ scopeLabel, changes }] }
  const groups = useMemo(() => {
    const byMember = new Map();
    byRow.forEach((rowChanges, rowKey) => {
      const [uid, sc] = rowKey.split(":");
      if (!byMember.has(uid)) byMember.set(uid, []);
      byMember.get(uid).push({ sc, rowChanges });
    });
    return [...byMember.entries()].map(([uid, scopes]) => {
      const m = memberById.get(uid);
      return {
        uid,
        memberLabel: m ? (m.name || m.email) : uid,
        memberSub: m ? m.email : "",
        scopes: scopes.map(({ sc, rowChanges }) => ({
          scopeLabel: sc === "room"
            ? "Room default"
            : (docById.get(sc)?.name || "Document"),
          isRoom: sc === "room",
          rowChanges,
        })),
      };
    });
  }, [byRow, memberById, docById]);

  return (
    <div
      className="fixed inset-0 z-[80] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4"
      data-testid="perm-review-modal"
      role="dialog"
      aria-modal="true"
      aria-label="Review pending permission changes"
      onClick={(e) => { if (e.target === e.currentTarget) onCancel(); }}
    >
      <div className="bg-card border border-border rounded-lg shadow-2xl w-full max-w-2xl max-h-[88vh] flex flex-col">
        {/* Head */}
        <div className="px-5 py-4 border-b border-border flex items-start justify-between gap-3">
          <div className="min-w-0">
            <h3 className="font-display tracking-tight text-lg">Review pending changes</h3>
            <p className="text-xs text-muted-foreground mt-1">
              {changes.length} change{changes.length === 1 ? "" : "s"} across {byRow.size} scope{byRow.size === 1 ? "" : "s"}.
              Apply will overwrite the affected permission rows on the server. This action is auditable in the Activity log.
            </p>
          </div>
          <button
            type="button"
            onClick={onCancel}
            className="p-1.5 rounded-md hover:bg-secondary transition shrink-0"
            aria-label="Close review"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-5">
          {groups.length === 0 && (
            <div className="text-sm text-muted-foreground">No pending changes.</div>
          )}
          {groups.map((g) => (
            <div key={g.uid} className="space-y-2">
              <div className="flex items-center gap-2">
                <div className="w-7 h-7 rounded-md bg-primary/15 text-primary flex items-center justify-center">
                  <UsersIcon className="w-3.5 h-3.5" />
                </div>
                <div className="min-w-0">
                  <div className="text-sm font-medium truncate">{g.memberLabel}</div>
                  {g.memberSub && g.memberSub !== g.memberLabel && (
                    <div className="text-xs text-muted-foreground truncate">{g.memberSub}</div>
                  )}
                </div>
              </div>
              <div className="ml-9 space-y-2">
                {g.scopes.map(({ scopeLabel, isRoom, rowChanges }) => (
                  <div key={scopeLabel} className="border border-border rounded-md bg-background/40">
                    <div className="px-3 py-2 border-b border-border flex items-center gap-2 label-mono text-muted-foreground">
                      {isRoom
                        ? <Shield className="w-3.5 h-3.5 text-primary" />
                        : <FileText className="w-3.5 h-3.5" />}
                      <span className="truncate">{scopeLabel}</span>
                    </div>
                    <ul className="divide-y divide-border">
                      {rowChanges.map((c) => (
                        <li key={`${c.rowKey}:${c.perm}`} className="px-3 py-2 flex items-center justify-between gap-3 text-sm">
                          <div className="min-w-0">
                            <div className="font-medium truncate">{PERMISSION_LABELS[c.perm].label}</div>
                            <div className="text-xs text-muted-foreground truncate">{PERMISSION_LABELS[c.perm].hint}</div>
                          </div>
                          <div className="flex items-center gap-2 shrink-0">
                            <PermBadge on={c.from} muted />
                            <span className="text-muted-foreground">→</span>
                            <PermBadge on={c.to} />
                          </div>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* Footer */}
        <div className="px-5 py-3 border-t border-border flex items-center justify-between gap-3 flex-wrap">
          <button
            type="button"
            onClick={onDiscard}
            disabled={applying || changes.length === 0}
            className="inline-flex items-center gap-1.5 px-3 py-2 text-sm rounded-md border border-border text-muted-foreground hover:bg-secondary hover:text-foreground transition disabled:opacity-60"
          >
            <RotateCcw className="w-4 h-4" /> Discard all
          </button>
          <div className="flex items-center gap-2 ml-auto">
            <button
              type="button"
              onClick={onCancel}
              disabled={applying}
              className="px-3 py-2 text-sm rounded-md border border-border hover:bg-secondary transition disabled:opacity-60"
            >
              Cancel
            </button>
            <button
              type="button"
              data-testid="perm-apply-confirm"
              onClick={onApply}
              disabled={applying || changes.length === 0}
              className="inline-flex items-center gap-1.5 px-4 py-2 text-sm rounded-md bg-primary text-primary-foreground hover:bg-primary/90 transition disabled:opacity-60"
            >
              <Save className="w-4 h-4" />
              {applying
                ? "Applying…"
                : `Apply ${changes.length} change${changes.length === 1 ? "" : "s"}`}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function PermBadge({ on, muted }) {
  if (on) {
    return (
      <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded mono text-[10px] uppercase tracking-wider ${
        muted ? "bg-secondary text-muted-foreground" : "bg-primary/15 text-primary"
      }`}>
        <CheckCircle2 className="w-3 h-3" /> On
      </span>
    );
  }
  return (
    <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded mono text-[10px] uppercase tracking-wider ${
      muted ? "bg-secondary text-muted-foreground" : "bg-destructive/15 text-destructive"
    }`}>
      <Circle className="w-3 h-3" /> Off
    </span>
  );
}
